import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useState, useCallback, useEffect, useRef } from 'react';
import ColorPalette from './ColorPalette';
import TextAlignSwitcher from './TextAlignSwitcher';
import { FontSelectorDialog } from './FontSelectorDialog';
import { useDispatch, useSelector } from 'react-redux';
import { CANVAS_TEXT_PROPERTY_CHANGED, MENU_OPTION_SELECTED, } from '../../Rutab/RutabConstants';
import { saveCanvasState } from '../../Actions/Rutab/HistoryActions';
import { debounce } from 'lodash-es';
const TextObjectMenu = memo(({ canvas, activeObject }) => {
    const [colorPaletteOpen, setColorPaletteOpen] = useState(false);
    const [fontSize, setFontSize] = useState(activeObject.fontSize);
    const [textStyleChanged, setTextStyle] = useState(false);
    const [isFontStyleDialogOpen, setFontStyleDialogOpen] = useState(false);
    const dispatch = useDispatch();
    const canvasTextProperty = useSelector((state) => state.decalEditor.canvasTextProperty);
    const textObjectMenuRef = useRef(true);
    useEffect(() => {
        if (textObjectMenuRef.current) {
            textObjectMenuRef.current = false;
            return;
        }
        setFontSize(activeObject.fontSize);
    }, [activeObject, canvasTextProperty]);
    const formatAlignText = (alignmentPosition) => {
        if (!activeObject)
            return;
        activeObject.set({
            textAlign: alignmentPosition,
        });
        activeObject.setCoords();
        canvas.renderAll();
        setTextStyle(!textStyleChanged);
        dispatch(saveCanvasState(canvas));
    };
    const updateFontSizeVisually = (newFontSize) => {
        if (!activeObject)
            return;
        activeObject.set({
            fontSize: newFontSize,
        });
        canvas.renderAll();
        setFontSize(newFontSize);
    };
    const debouncedSaveFontSizeChanges = useCallback(debounce((newFontSize, objText) => {
        if (!canvas)
            return;
        dispatch({
            type: CANVAS_TEXT_PROPERTY_CHANGED,
            payload: {
                canvasTextProperty: { fontSize: newFontSize, text: objText },
            },
        });
        dispatch(saveCanvasState(canvas));
    }, 500), [dispatch]);
    const changeFontSize = (fontSize) => {
        if (!activeObject)
            return;
        updateFontSizeVisually(fontSize);
        debouncedSaveFontSizeChanges(fontSize, activeObject.text);
    };
    const debouncedColorChange = debounce(() => {
        dispatch(saveCanvasState(canvas));
    }, 300);
    const changeTextColor = (color) => {
        if (!activeObject)
            return;
        activeObject.set({
            fill: color,
        });
        canvas.renderAll();
        debouncedColorChange();
    };
    const toggleTextStyle = (style) => {
        if (!activeObject)
            return;
        if (style === 'bold') {
            activeObject.set({
                fontWeight: activeObject.fontWeight === 'bold' ? 'normal' : 'bold',
            });
        }
        else if (style === 'underline') {
            activeObject.set({
                underline: !activeObject.underline,
            });
        }
        else if (style === 'italic') {
            activeObject.set({
                fontStyle: activeObject.fontStyle === 'italic' ? 'normal' : 'italic',
            });
        }
        setTextStyle(!textStyleChanged);
        canvas.renderAll();
        dispatch(saveCanvasState(canvas));
    };
    const toggleFontStyleDialog = useCallback(() => {
        const isOpen = !isFontStyleDialogOpen;
        if (isOpen) {
            dispatch({
                type: MENU_OPTION_SELECTED,
                payload: { menuOption: null },
            });
        }
        setFontStyleDialogOpen(!isFontStyleDialogOpen);
    }, [isFontStyleDialogOpen, dispatch]);
    return (_jsxs(_Fragment, { children: [_jsx("label", { className: "font-style-label cursor-pointer", onClick: () => {
                    toggleFontStyleDialog();
                }, children: activeObject.fontFamily }), _jsx("label", { className: `text-styling bold-action ${activeObject.fontWeight === 'bold' ? 'active' : ''}`, onClick: () => toggleTextStyle('bold'), children: "B" }), _jsx("label", { className: `text-styling underline-action ${activeObject.underline ? 'active' : ''}`, onClick: () => toggleTextStyle('underline'), children: "U" }), _jsx("label", { className: `text-styling italic-action ${activeObject.fontStyle === 'italic' ? 'active' : ''}`, onClick: () => toggleTextStyle('italic'), children: "I" }), _jsx("div", { className: "vertical-line" }), _jsx("div", { style: {
                    position: 'relative',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    borderRadius: '50%',
                    border: '1px solid #000',
                    background: activeObject.fill,
                }, onClick: () => setColorPaletteOpen(!colorPaletteOpen) }), _jsx("div", { className: "vertical-line" }), _jsxs("div", { className: "input-container", children: [_jsx("label", { className: "input-label", onClick: () => changeFontSize(fontSize - 1), children: "-" }), _jsx("input", { type: "number", className: "input-font-size", value: fontSize, min: 10, max: 1000, onChange: (e) => changeFontSize(parseInt(e.target.value, 10)) }), _jsx("label", { className: "input-label", onClick: () => changeFontSize(fontSize + 1), children: "+" })] }), _jsx("div", { className: "vertical-line" }), _jsx(TextAlignSwitcher, { currentAlign: activeObject.textAlign, onChange: (align) => {
                    formatAlignText(align);
                } }), colorPaletteOpen && (_jsx("div", { className: "selected-object-color-selector", style: {
                    top: `${-95}px`,
                    left: `${0}px`,
                    width: '320px',
                }, children: _jsx(ColorPalette, { selectedColor: activeObject.fill, onColorChange: changeTextColor }) })), isFontStyleDialogOpen && (_jsx(FontSelectorDialog, { canvas: canvas, activeTextObject: activeObject, onClose: toggleFontStyleDialog }))] }));
});
export default TextObjectMenu;
